import { useEffect } from 'react';

import { type AgentSessionResponse } from '@air/server-sdk';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isWorkspaceAgentSessionNotIdle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionNotIdle';
import {
  getNextWorkspaceAgentRestPollDelay,
  WORKSPACE_AGENT_REST_POLL_INITIAL_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentRestPoll';

export type UsePrivateWorkspaceAgentMyTasksSessionRefreshParams = {
  refreshSessions: () => Promise<void>;
  sessions: readonly Pick<AgentSessionResponse, 'id' | 'status'>[];
};

const refreshMyTasksSessions = (refreshSessions: () => Promise<void>): void => {
  void refreshSessions().catch((error) => {
    reportErrorToBugsnag({
      error,
      context: 'Failed to refresh workspace agent sessions when viewing My Tasks',
    });
  });
};

/**
 * My Tasks rows come from the sessions list. Refetch on open, then while any listed session is
 * still working or waiting: 5s, doubling up to 5 minutes. Going idle and live again resets to 5s.
 */
export const usePrivateWorkspaceAgentMyTasksSessionRefresh = ({
  refreshSessions,
  sessions,
}: UsePrivateWorkspaceAgentMyTasksSessionRefreshParams): void => {
  const hasNonIdleSession = sessions.some(isWorkspaceAgentSessionNotIdle);

  useEffect(() => {
    refreshMyTasksSessions(refreshSessions);
  }, [refreshSessions]);

  useEffect(() => {
    if (!hasNonIdleSession) {
      return;
    }

    let isCancelled = false;
    let timeoutId = 0;
    let delayMs = WORKSPACE_AGENT_REST_POLL_INITIAL_MS;

    const schedule = () => {
      timeoutId = window.setTimeout(() => {
        if (isCancelled) {
          return;
        }

        void refreshSessions()
          .catch((error) => {
            reportErrorToBugsnag({
              error,
              context: 'Failed to refresh workspace agent sessions when viewing My Tasks',
            });
          })
          .finally(() => {
            if (isCancelled) {
              return;
            }

            delayMs = getNextWorkspaceAgentRestPollDelay(delayMs);
            schedule();
          });
      }, delayMs);
    };

    schedule();

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [hasNonIdleSession, refreshSessions]);
};
